import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  LinearProgress,
} from '@mui/material';
import { UnifiedTableData } from '../../types/unifiedSchema';

interface TableOverviewSectionProps {
  table: UnifiedTableData;
}

const TableOverviewSection: React.FC<TableOverviewSectionProps> = ({ table }) => {
  const columns = table.columns || [];
  const avgNull =
    columns.length > 0
      ? columns.reduce((sum, c) => sum + (c.null_percentage || 0), 0) / columns.length
      : 0;

  // Data type breakdown
  const typeCounts: Record<string, number> = {};
  columns.forEach((c) => {
    const t = (c.data_type || 'unknown').toLowerCase();
    typeCounts[t] = (typeCounts[t] || 0) + 1;
  });

  const piiCount = columns.filter((c) => (c.pii_likelihood || 0) > 0.5).length;

  return (
    <Grid container spacing={2}>
      <Grid item xs={6} sm={3}>
        <Card variant="outlined">
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">
              Rows
            </Typography>
            <Typography variant="h6">
              {table.row_count.toLocaleString()}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={6} sm={3}>
        <Card variant="outlined">
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">
              Columns
            </Typography>
            <Typography variant="h6">{columns.length}</Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={6} sm={3}>
        <Card variant="outlined">
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">
              Avg Null %
            </Typography>
            <Typography variant="h6">{avgNull.toFixed(1)}%</Typography>
            <LinearProgress
              variant="determinate"
              value={avgNull}
              color={avgNull > 30 ? 'warning' : 'success'}
              sx={{ mt: 0.5 }}
            />
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={6} sm={3}>
        <Card variant="outlined">
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">
              File Size
            </Typography>
            <Typography variant="h6">{table.file_size_mb.toFixed(1)} MB</Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
            Types:
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }} component="span">
            {Object.entries(typeCounts).map(([type, count]) => (
              <Chip
                key={type}
                label={`${type} (${count})`}
                size="small"
                variant="outlined"
              />
            ))}
          </Stack>
          {piiCount > 0 && (
            <Chip
              label={`${piiCount} PII`}
              size="small"
              color="warning"
              sx={{ ml: 1 }}
            />
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export default TableOverviewSection;
